/**
 * What the dashboard knows about common desktop applications, independent of
 * anyone's category rules:
 *
 *  - which processes are the operating system's own shell rather than work
 *    (the taskbar, Start, search, notifications, the lock screen). Their time
 *    is left out of activity views by default, though the evidence itself is
 *    untouched and the Diagnostics page still shows it;
 *  - a suggested category for well-known applications, used only when the
 *    organization's own rules leave an event Uncategorized.
 *
 * Matching is by executable name (`bundle_id` in the interval contract, e.g.
 * `Code.exe`), which is stable across versions and locales, unlike the
 * display name. Legacy payloads carried only the executable as `app`, so that
 * is checked too.
 */
import { classifyEvents } from '~/util/classes';
import { labelFor } from '~/util/engineLabels';
/**
 * Windows shell and system host processes. These are frontmost when someone
 * clicks the taskbar or desktop, opens Start, search or notifications,
 * switches windows, types into an on-screen or IME panel, or is at the lock or
 * sign-in screen. None of it is an application being used.
 */
export const SYSTEM_PROCESSES = [
    'explorer.exe', // Windows Explorer: desktop, taskbar, and File Explorer
    'shellexperiencehost.exe', // Windows Shell Experience Host: notifications, calendar flyout
    'startmenuexperiencehost.exe', // Start menu
    'searchhost.exe', // Search (Windows 11)
    'searchapp.exe', // Search (Windows 10)
    'searchui.exe', // Cortana search (older Windows 10)
    'lockapp.exe', // lock screen
    'logonui.exe', // sign-in screen
    'consent.exe', // UAC elevation prompt
    'textinputhost.exe', // touch keyboard, emoji and IME panels
    'dwm.exe', // Desktop Window Manager
    'sihost.exe', // Shell Infrastructure Host
    'shellhost.exe', // Windows 11 shell host (quick settings)
    'widgets.exe', // Widgets board
    'taskswitcher.exe', // Alt+Tab switcher on some builds
];
/** Hosts whose window title names the real app (Settings, Calculator, and
 * other packaged apps run inside ApplicationFrameHost). */
const FRAME_HOSTS = ['applicationframehost.exe'];
const CATALOG = [
    {
        category: ['Work', 'Programming'],
        executables: [
            'code.exe',
            'code - insiders.exe',
            'cursor.exe',
            'windsurf.exe',
            'zed.exe',
            'devenv.exe',
            'idea64.exe',
            'pycharm64.exe',
            'webstorm64.exe',
            'rider64.exe',
            'clion64.exe',
            'goland64.exe',
            'phpstorm64.exe',
            'rustrover64.exe',
            'datagrip64.exe',
            'studio64.exe',
            'sublime_text.exe',
            'notepad++.exe',
            'windowsterminal.exe',
            'wt.exe',
            'powershell.exe',
            'pwsh.exe',
            'cmd.exe',
            'openconsole.exe',
            'mintty.exe',
            'git-bash.exe',
            'githubdesktop.exe',
            'sourcetree.exe',
            'gitkraken.exe',
            'fork.exe',
            'postman.exe',
            'insomnia.exe',
            'docker desktop.exe',
            'dbeaver.exe',
            'ssms.exe',
            'azuredatastudio.exe',
        ],
    },
    {
        category: ['Work', 'Documents'],
        executables: [
            'winword.exe',
            'excel.exe',
            'powerpnt.exe',
            'onenote.exe',
            'onenotem.exe',
            'msaccess.exe',
            'mspub.exe',
            'visio.exe',
            'winproj.exe',
            'acrobat.exe',
            'acrord32.exe',
            'soffice.exe',
            'soffice.bin',
            'notion.exe',
            'obsidian.exe',
            'evernote.exe',
            'notepad.exe',
        ],
    },
    {
        category: ['Work', 'Image'],
        executables: [
            'photoshop.exe',
            'illustrator.exe',
            'figma.exe',
            'inkscape.exe',
            'gimp-2.10.exe',
            'paintdotnet.exe',
            'mspaint.exe',
        ],
    },
    {
        category: ['Work', 'Video'],
        executables: ['adobe premiere pro.exe', 'resolve.exe', 'kdenlive.exe'],
    },
    { category: ['Work', '3D'], executables: ['blender.exe'] },
    {
        category: ['Comms', 'Email'],
        executables: ['outlook.exe', 'olk.exe', 'hxoutlook.exe', 'thunderbird.exe', 'mailspring.exe'],
    },
    {
        category: ['Comms', 'IM'],
        executables: [
            'ms-teams.exe',
            'teams.exe',
            'slack.exe',
            'discord.exe',
            'telegram.exe',
            'whatsapp.exe',
            'signal.exe',
            'skype.exe',
            'element.exe',
        ],
    },
    {
        category: ['Comms', 'Meetings'],
        executables: ['zoom.exe', 'webex.exe', 'ciscocollabhost.exe'],
    },
    { category: ['Media', 'Music'], executables: ['spotify.exe', 'itunes.exe', 'applemusic.exe'] },
    { category: ['Media', 'Video'], executables: ['vlc.exe', 'mpc-hc64.exe', 'potplayermini64.exe'] },
    {
        category: ['Media', 'Games'],
        executables: ['steam.exe', 'steamwebhelper.exe', 'epicgameslauncher.exe', 'battle.net.exe'],
    },
];
const CATEGORY_BY_EXECUTABLE = new Map(CATALOG.flatMap(entry => entry.executables.map(exe => [exe, entry.category])));
export const UNCATEGORIZED = 'Uncategorized';
/** The engine's word for time its rules gave no label to on that day (or a
 * day it hasn't looked at yet). */
export const NOT_YET_NAMED = ['Not yet named'];
/** Lowercased identifiers an event can be matched by: its executable, then
 * its display name. */
function identifiers(event) {
    return [event.bundle_id, event.app]
        .filter((v) => typeof v === 'string' && v.length > 0)
        .map(v => v.toLowerCase());
}
export function isHiddenApp(event, options) {
    const ids = identifiers(event);
    if (options.hideSystemApps && ids.some(id => SYSTEM_PROCESSES.includes(id)))
        return true;
    const hidden = options.hiddenApps.map(a => a.toLowerCase());
    return ids.some(id => hidden.includes(id));
}
/** The name an app is shown under: a frame host takes its window's title,
 * since "Application Frame Host" says nothing about what was in use. */
export function displayApp(event) {
    const exe = event.bundle_id?.toLowerCase();
    if (exe && FRAME_HOSTS.includes(exe) && event.title)
        return event.title;
    return event.app;
}
/**
 * Payloads as the activity views should see them: hidden apps' events left
 * out (their seconds counted, not silently lost) and frame-hosted apps
 * shown under their real name. Idle (AFK) events are kept whole. Returns new
 * objects; the decrypted originals are never modified.
 */
export function visibleActivity(payloads, options) {
    let hiddenSeconds = 0;
    const visible = payloads.map(payload => ({
        ...payload,
        events: payload.events.flatMap(event => {
            if (!event.afk && isHiddenApp(event, options)) {
                hiddenSeconds += event.duration_secs;
                return [];
            }
            const app = displayApp(event);
            return [
                app === event.app
                    ? event
                    : { ...event, app, recorded_app: event.recorded_app ?? event.app },
            ];
        }),
    }));
    return { payloads: visible, hiddenSeconds };
}
/** The catalog's suggested category for an app, resolved to the most
 * specific path that exists in `existing` (category paths joined by '>'), or
 * null when the app is unknown or no suitable category exists. */
export function suggestedCategory(event, existing) {
    for (const id of identifiers(event)) {
        const path = CATEGORY_BY_EXECUTABLE.get(id);
        if (!path)
            continue;
        for (let depth = path.length; depth > 0; depth--) {
            const candidate = path.slice(0, depth);
            if (existing.has(candidate.join('>')))
                return candidate;
        }
    }
    return null;
}
/**
 * The organization's rules first, then the catalog for whatever they leave
 * Uncategorized. An event classified by the catalog is marked with
 * `$category_source: 'suggested'` so a view can say so.
 */
export function classifyDeviceEvents(events, categories) {
    // With the engine's labels at hand, they are the only categories: no
    // rules of this app's own, no catalog suggestions.
    const engine = categories.find(c => c.rule.type === 'engine' && c.rule.lookup);
    if (engine?.rule.lookup) {
        const lookup = engine.rule.lookup;
        return events.map(event => ({
            ...event,
            data: { ...event.data, $category: labelFor(lookup, event) },
        }));
    }
    const existing = new Set(categories.map(c => c.name.join('>')));
    return classifyEvents(events, categories).map(event => {
        const category = event.data.$category;
        const uncategorized = !category || (category.length === 1 && category[0] === UNCATEGORIZED);
        if (!uncategorized)
            return event;
        const suggestion = suggestedCategory({ app: event.data.app ?? null, bundle_id: event.data.bundle_id ?? null }, existing);
        if (!suggestion)
            return event;
        return {
            ...event,
            data: { ...event.data, $category: suggestion, $category_source: 'suggested' },
        };
    });
}
